import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLatestPosts } from '../hooks/usePosts';

const verticalLinesPattern = {
  backgroundImage:
    'repeating-linear-gradient(90deg, #D4512A 0, #D4512A 1px, transparent 1px, transparent 96px)',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function Blog() {
  const { data: posts = [] } = useLatestPosts(10);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // Mostra cada seta só quando há cards para rolar naquela direção.
  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanLeft(track.scrollLeft > 2);
    setCanRight(track.scrollLeft + track.clientWidth < track.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    updateArrows();
    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      track.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, posts.length]);

  if (posts.length === 0) return null;

  const scrollByCard = (dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    // ~largura de um card + gap; usa o primeiro filho como referência.
    const card = track.firstElementChild as HTMLElement | null;
    const amount = card ? card.offsetWidth + 24 : track.clientWidth * 0.8;
    track.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  return (
    <section id="blog" className="relative py-20 md:py-24 bg-neutral-50 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={verticalLinesPattern}
      />

      <div className="section-container relative">
        {/* Header editorial */}
        <motion.div
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-48px' }}
          transition={{ duration: 0.55 }}
        >
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <motion.span
                className="h-px bg-orange origin-left"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                style={{ width: 40 }}
              />
              <span className="text-caption font-semibold uppercase tracking-[0.22em] text-neutral-700">
                Insights
              </span>
            </div>
            <h2 className="text-display-sm md:text-display-md font-bold text-neutral-950 leading-tight tracking-tight">
              Inteligência jurídica para o setor educacional.
            </h2>
          </div>
        </motion.div>

        {/* Carrossel de artigos — mais novo → mais antigo */}
        <div className="flex items-center gap-2 lg:gap-4">
          {/* Seta esquerda — fora do carrossel, visível só quando há cards à esquerda */}
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            disabled={!canLeft}
            aria-label="Artigos anteriores"
            className="hidden md:flex shrink-0 h-12 w-7 items-center justify-center text-neutral-400 hover:text-orange-600 transition-colors disabled:opacity-0 disabled:pointer-events-none"
          >
            <ChevronLeft size={26} />
          </button>

          <div
            ref={trackRef}
            className="flex-1 min-w-0 flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
          {posts.map((post, i) => (
            <article
              key={post.id}
              className="group relative snap-start flex-shrink-0 w-[80vw] sm:w-[340px] bg-white border border-neutral-200 transition-all duration-200 hover:border-orange-200 hover:shadow-[0_20px_40px_-16px_rgba(15,23,42,0.12)]"
            >
              <Link to={`/blog/${post.slug}`} className="absolute inset-0 z-10" aria-label={post.title} />
              {/* Cover editorial — número da edição + data */}
              <div className="relative h-40 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 overflow-hidden">
                {/* Pattern interno */}
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-[0.08]"
                  style={verticalLinesPattern}
                />
                {/* Número da edição */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-display-2xl font-bold text-white/[0.06] tabular-nums leading-none select-none">
                    {String(posts.length - i).padStart(2, '0')}
                  </span>
                </div>
                {/* Meta no topo */}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                  <span className="text-caption font-semibold uppercase tracking-[0.18em] text-orange-300">
                    {post.tags[0]}
                  </span>
                  <span className="text-caption uppercase tracking-wider text-neutral-400 tabular-nums">
                    {formatDate(post.publishedAt)}
                  </span>
                </div>
                {/* Linha decorativa laranja na base */}
                <div
                  aria-hidden
                  className="absolute bottom-0 left-0 h-0.5 bg-orange transition-all duration-300 w-8 group-hover:w-full"
                />
              </div>

              {/* Conteúdo */}
              <div className="flex flex-col p-6">
                <h3 className="font-semibold text-heading-md text-neutral-950 leading-snug mb-3 group-hover:text-orange-700 transition-colors min-h-[3.4em]">
                  {post.title}
                </h3>

                <p className="text-body-sm text-neutral-500 leading-relaxed mb-5 line-clamp-3">
                  {post.excerpt}
                </p>

                {/* Footer do card */}
                <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
                  <span className="text-caption uppercase tracking-wider text-neutral-400">
                    {post.author.split(' ')[0]} {post.author.split(' ').slice(-1)}
                  </span>
                  <ArrowUpRight
                    size={16}
                    className="text-neutral-400 group-hover:text-orange-600 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </div>
              </div>
            </article>
          ))}
          </div>

          {/* Seta direita — fora do carrossel, visível só quando há cards à direita */}
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            disabled={!canRight}
            aria-label="Próximos artigos"
            className="hidden md:flex shrink-0 h-12 w-7 items-center justify-center text-neutral-400 hover:text-orange-600 transition-colors disabled:opacity-0 disabled:pointer-events-none"
          >
            <ChevronRight size={26} />
          </button>
        </div>

        {/* CTA fixo abaixo do carrossel */}
        <div className="mt-10 flex justify-center">
          <Link
            to="/blog"
            className="group inline-flex items-center gap-2 rounded-xl border border-neutral-300 bg-white px-6 py-3 text-body-sm font-semibold text-neutral-800 hover:border-orange-300 hover:text-orange-700 transition-colors"
          >
            Ver todos os artigos
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
